import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Home, Search, MapPin, Newspaper, Plus, User, Heart, LogOut, Menu, X, Globe, ChevronDown, LayoutDashboard, Building, Calculator, GitCompareArrows, FileText } from 'lucide-react';
import { NotificationBell } from './NotificationBell';

export const Navbar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-slate-100 shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <div className="w-9 h-9 bg-blue-900 rounded-lg flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-manrope font-bold text-xl text-blue-900 leading-none">Beytam</span>
              <span className="text-[10px] text-slate-400 font-arabic">بيتم</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/search?transaction_type=vente"
              className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-blue-900 hover:bg-slate-50 rounded-lg transition-colors"
              data-testid="nav-buy"
            >
              {t('search_buy')}
            </Link>
            <Link
              to="/search?transaction_type=location"
              className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-blue-900 hover:bg-slate-50 rounded-lg transition-colors"
              data-testid="nav-rent"
            >
              {t('search_rent')}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-blue-900 hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-1" data-testid="nav-tools-menu">
                  <MapPin className="w-4 h-4" />
                  {language === 'fr' ? 'Prix & Outils' : 'الأسعار والأدوات'}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                <DropdownMenuItem asChild>
                  <Link to="/prix-immobilier" className="flex items-center gap-2" data-testid="nav-prices">
                    <MapPin className="w-4 h-4" />
                    {t('nav_price_map')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/estimer" className="flex items-center gap-2" data-testid="nav-estimate">
                    <Calculator className="w-4 h-4" />
                    {language === 'fr' ? 'Estimer un bien' : 'تقييم عقار'}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/comparer" className="flex items-center gap-2" data-testid="nav-compare">
                    <GitCompareArrows className="w-4 h-4" />
                    {language === 'fr' ? 'Comparer les quartiers' : 'مقارنة الأحياء'}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link
              to="/blog"
              className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-blue-900 hover:bg-slate-50 rounded-lg transition-colors"
              data-testid="nav-blog"
            >
              {t('nav_blog')}
            </Link>
            <Link
              to="/agencies"
              className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-blue-900 hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-1"
              data-testid="nav-agencies"
            >
              <Building className="w-4 h-4" />
              {language === 'fr' ? 'Agences' : 'الوكالات'}
            </Link>
          </div>

          {/* Right side actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-blue-900 transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              {language === 'fr' ? 'العربية' : 'FR'}
            </button>

            {isAuthenticated ? (
              <>
                <NotificationBell />
                <Link to="/add-property">
                  <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-5" data-testid="add-property-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('nav_post_ad')}
                  </Button>
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-900" />
                      </div>
                      <span className="hidden xl:inline">{user?.full_name?.split(' ')[0]}</span>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link">
                        <LayoutDashboard className="w-4 h-4" />
                        {language === 'fr' ? 'Tableau de bord' : 'لوحة التحكم'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="my-properties-link">
                        <Home className="w-4 h-4" />
                        {t('nav_my_ads')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="favorites-link">
                        <Heart className="w-4 h-4" />
                        {t('nav_favorites')}
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2" data-testid="blog-admin-link">
                            <FileText className="w-4 h-4" />
                            {language === 'fr' ? 'Administration' : 'الإدارة'}
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-rose-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('nav_logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-slate-700" data-testid="login-btn">
                    {t('nav_login')}
                  </Button>
                </Link>
                <Link to="/add-property">
                  <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-5" data-testid="register-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('nav_post_ad')}
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              <Link
                to="/search?transaction_type=vente"
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('search_buy')}
              </Link>
              <Link
                to="/search?transaction_type=location"
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('search_rent')}
              </Link>
              <Link
                to="/prix-immobilier"
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <MapPin className="w-4 h-4" />
                {t('nav_price_map')}
              </Link>
              <Link
                to="/estimer"
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Calculator className="w-4 h-4" />
                {language === 'fr' ? 'Estimer' : 'تقييم'}
              </Link>
              <Link
                to="/comparer"
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <GitCompareArrows className="w-4 h-4" />
                {language === 'fr' ? 'Comparer' : 'مقارنة'}
              </Link>
              <Link
                to="/blog"
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav_blog')}
              </Link>
              <Link
                to="/agencies"
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Building className="w-4 h-4" />
                {language === 'fr' ? 'Agences' : 'الوكالات'}
              </Link>
              
              <div className="border-t border-slate-100 my-2" />
              
              <button
                onClick={() => { toggleLanguage(); setMobileMenuOpen(false); }}
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
              >
                <Globe className="w-4 h-4" />
                {language === 'fr' ? 'العربية' : 'Français'}
              </button>
              
              {isAuthenticated ? (
                <>
                  <Link to="/add-property" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full mt-2">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('nav_post_ad')}
                    </Button>
                  </Link>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2">
                    <LayoutDashboard className="w-4 h-4" />
                    {language === 'fr' ? 'Tableau de bord' : 'لوحة التحكم'}
                  </Link>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    {t('nav_my_ads')}
                  </Link>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    {t('nav_favorites')}
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-blue-900 hover:bg-blue-50 rounded-lg flex items-center gap-2 font-medium">
                      <FileText className="w-4 h-4" />
                      {language === 'fr' ? 'Administration' : 'الإدارة'}
                    </Link>
                  )}
                  <button onClick={handleLogout} className="px-4 py-3 text-rose-600 hover:bg-slate-50 rounded-lg flex items-center gap-2">
                    <LogOut className="w-4 h-4" />
                    {t('nav_logout')}
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-4 mt-2">
                  <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">{t('nav_login')}</Button>
                  </Link>
                  <Link to="/register" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-blue-900">{t('nav_register')}</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
